import React, { useEffect, useState } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Calendar, DollarSign, Play, Pause, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Campaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    campaign_name: '',
    platforms: [],
    budget: '',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [campaignsRes, platformsRes] = await Promise.all([
        axios.get(`${API}/campaigns`, { withCredentials: true }),
        axios.get(`${API}/platforms`, { withCredentials: true })
      ]);
      setCampaigns(campaignsRes.data);
      setPlatforms(platformsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/campaigns`, formData, {
        withCredentials: true
      });
      toast.success('Campaign created successfully');
      setIsDialogOpen(false);
      setFormData({
        campaign_name: '',
        platforms: [],
        budget: '',
        start_date: '',
        end_date: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error creating campaign:', error);
      toast.error('Failed to create campaign');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/10 text-green-500';
      case 'paused':
        return 'bg-yellow-500/10 text-yellow-500';
      case 'draft':
        return 'bg-gray-500/10 text-gray-500';
      default:
        return 'bg-blue-500/10 text-blue-500';
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading campaigns...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-8" data-testid="campaigns-page">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">Campaign Planning</h1>
            <p className="text-muted-foreground">Plan and manage cross-channel advertising campaigns</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-campaign-button" className="bg-primary hover:bg-primary/90">
                <Plus className="w-4 h-4 mr-2" />
                New Campaign
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create New Campaign</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateCampaign} className="space-y-4">
                <div>
                  <Label htmlFor="campaign_name">Campaign Name</Label>
                  <Input
                    id="campaign_name"
                    data-testid="campaign-name-input"
                    value={formData.campaign_name}
                    onChange={(e) => setFormData({ ...formData, campaign_name: e.target.value })}
                    placeholder="e.g., Q1 2026 Launch Campaign"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="budget">Budget ($)</Label>
                  <Input
                    id="budget"
                    type="number"
                    data-testid="campaign-budget-input"
                    value={formData.budget}
                    onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                    placeholder="10000"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="start_date">Start Date</Label>
                    <Input
                      id="start_date"
                      type="date"
                      data-testid="campaign-start-date-input"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="end_date">End Date</Label>
                    <Input
                      id="end_date"
                      type="date"
                      data-testid="campaign-end-date-input"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <Button type="submit" data-testid="submit-campaign-button" className="w-full">
                  Create Campaign
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Campaigns List */}
        {campaigns.length === 0 ? (
          <Card className="p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Plus className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-bold mb-2">No Campaigns Yet</h3>
              <p className="text-muted-foreground mb-6">
                Start planning your first cross-channel advertising campaign
              </p>
              <Button onClick={() => setIsDialogOpen(true)} data-testid="empty-state-create-button">
                Create Campaign
              </Button>
            </div>
          </Card>
        ) : (
          <div className="space-y-4">
            {campaigns.map((campaign) => (
              <Card key={campaign.campaign_id} data-testid={`campaign-card-${campaign.campaign_id}`} className="p-6 hover:border-primary/50 transition-colors">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-xl font-bold">{campaign.campaign_name}</h3>
                      <span className={`text-xs px-2 py-1 rounded ${getStatusColor(campaign.status)}`}>
                        {campaign.status}
                      </span>
                    </div>
                    <div className="flex items-center space-x-6 text-sm text-muted-foreground">
                      <div className="flex items-center space-x-2">
                        <DollarSign className="w-4 h-4" />
                        <span>Budget: ${campaign.budget?.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4" />
                        <span>
                          {new Date(campaign.start_date).toLocaleDateString()} - {new Date(campaign.end_date).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <TrendingUp className="w-4 h-4" />
                        <span>{campaign.platforms?.length || 0} platforms</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    {campaign.status === 'draft' && (
                      <Button size="sm" variant="outline">
                        <Play className="w-4 h-4 mr-1" />
                        Launch
                      </Button>
                    )}
                    {campaign.status === 'active' && (
                      <Button size="sm" variant="outline">
                        <Pause className="w-4 h-4 mr-1" />
                        Pause
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* Mock Performance Metrics */}
                <div className="grid grid-cols-4 gap-4 pt-4 border-t border-border">
                  <div>
                    <div className="text-sm text-muted-foreground">Impressions</div>
                    <div className="text-lg font-bold">1.2M</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Clicks</div>
                    <div className="text-lg font-bold">24.5K</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">CTR</div>
                    <div className="text-lg font-bold">2.04%</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Conversions</div>
                    <div className="text-lg font-bold">892</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Campaign Planning Tips */}
        <Card className="p-6 mt-8 bg-primary/5 border-primary/20">
          <h3 className="text-lg font-bold mb-2">Campaign Planning Tips</h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li>• Start with clear objectives and KPIs for each campaign</li>
            <li>• Allocate budget based on platform performance history</li>
            <li>• Test multiple creatives and messaging variations</li>
            <li>• Monitor performance daily during the first week</li>
            <li>• Use A/B testing to optimize conversion rates</li>
          </ul>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Campaigns;
